import { Component, Input, Output, EventEmitter } from '@angular/core';
import { AgentHubItem, CategoryColorVariant } from '../../models';
import { RegulationsHubRegRadarService } from '../../services/regulations-hub-reg-radar/regulations-hub-reg-radar.service';

/**
 * Agent Card Component
 * 
 * Displays an agent as a card in the agent hub grid.
 * Includes agent icon, name, description, category badge,
 * and hover actions (info and favorite icons).
 * 
 * Features:
 * - Agent icon with fallback
 * - Title and description
 * - Category badge with color variant
 * - Hover state with info and favorite icons
 * - Favorite toggle with visual feedback
 * - Click to select agent
 * 
 * Usage:
 * <app-agent-card
 *   [agent]="agentItem"
 *   [isFavorite]="true"
 *   (favoriteToggled)="onFavoriteToggle($event)"
 *   (infoClicked)="onInfoClick($event)"
 *   (cardClicked)="onCardClick($event)">
 * </app-agent-card>
 */
@Component({
  selector: 'app-agent-card',
  templateUrl: './agent-card.component.html',
  styleUrls: ['./agent-card.component.scss']
})
export class AgentCardComponent {
  /**
   * Agent data to display
   */
  @Input() agent!: AgentHubItem;

  /**
   * Whether the agent is marked as favorite
   */
  @Input() isFavorite: boolean = false;

  /**
   * Whether the agent is in the default section
   */
  @Input() isDefault: boolean = false;

  /**
   * Whether to show the pin icon (only in Default section)
   */
  @Input() showPinIcon: boolean = false;

  /**
   * Emits agent ID when favorite is toggled
   */
  @Output() favoriteToggled = new EventEmitter<string>();

  /**
   * Emits agent ID when info icon is clicked
   */
  @Output() infoClicked = new EventEmitter<string>();

  /**
   * Emits agent when card is clicked
   */
  @Output() cardClicked = new EventEmitter<AgentHubItem>();

  /**
   * Emits agent ID when unpin (remove from default) is clicked
   */
  @Output() unpinClicked = new EventEmitter<string>();

  /**
   * Optional chatbot instance (when agent hub is embedded with chatbot) for
   * user/tenant/domain used by RegRadar API; otherwise environment is used.
   */
  @Input() chatbotComponentRef: any = null;

  /** Tracks hover state for showing action icons */
  isHovered: boolean = false;

  constructor(private readonly regulationsHubRegRadarService: RegulationsHubRegRadarService) {}

  /**
   * Handle mouse enter event
   */
  onMouseEnter(): void {
    this.isHovered = true;
  }

  /**
   * Handle mouse leave event
   */
  onMouseLeave(): void {
    this.isHovered = false;
  }

  /**
   * Handle card click
   * 
   * @param event - Mouse event
   */
  onCardClick(event: MouseEvent): void {
    // Don't trigger card click if clicking on action buttons
    const target = event.target as HTMLElement;
    if (target.closest('.card-actions')) {
      return;
    }
    if (this.isRegulationsAgentCard()) {
      this.regulationsHubRegRadarService.loadHubRegRadarSliceForRegulationsAgent(this.chatbotComponentRef);
    }
    this.cardClicked.emit(this.agent);
  }

  /**
   * Handle keyboard navigation on card
   * 
   * @param event - Keyboard event
   */
  onCardKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (this.isRegulationsAgentCard()) {
        this.regulationsHubRegRadarService.loadHubRegRadarSliceForRegulationsAgent(this.chatbotComponentRef);
      }
      this.cardClicked.emit(this.agent);
    }
  }

  /** Same agent id used across the app for the regulations experience. */
  private isRegulationsAgentCard(): boolean {
    return this.agent?.id === 'regulations_agent';
  }

  /**
   * Toggle favorite status
   * 
   * @param event - Mouse event
   */
  onFavoriteClick(event: MouseEvent): void {
    event.stopPropagation();
    this.favoriteToggled.emit(this.agent.id);
  }

  /**
   * Handle info icon click
   * 
   * @param event - Mouse event
   */
  onInfoClick(event: MouseEvent): void {
    event.stopPropagation();
    this.infoClicked.emit(this.agent.id);
  }

  /**
   * Handle unpin (remove from default) click
   * 
   * @param event - Mouse event
   */
  onUnpinClick(event: MouseEvent): void {
    event.stopPropagation();
    this.unpinClicked.emit(this.agent.id);
  }

  /**
   * Get the icon source with fallback
   * 
   * @returns string - Icon path or fallback
   */
  get iconSrc(): string {
    return this.agent?.icon || 'assets/icons/artificial-intelligence.svg';
  }

  /**
   * Get the category display name
   * 
   * @returns string - Category name
   */
  get categoryName(): string {
    return this.agent?.category?.name || 'General';
  }

  /**
   * Get the category color variant
   * 
   * @returns CategoryColorVariant - Color variant
   */
  get categoryColor(): CategoryColorVariant {
    return this.agent?.category?.colorVariant || 'blue';
  }
}
